// homeAnalytics.ts — CORE-595 (epic CORE-482 «Homepage Redesign»)
//
// Typed catalog + emit helpers for the new-home analytics described in §9 of
// the spec «Новая главная страница Agapella (общая концепция)». Thin layer over
// `analytics.track(...)` (→ the existing `AnalyticsEvents` store) — NOT a new
// stream, exactly like `liveSessionAnalytics.ts` (CORE-559).
//
// ── Why this file exists separately from the emit sites ──────────────────────
// CORE-595 is «сведение единых параметров, проверка доставки и дашборд»; the
// emitting itself belongs to the frontend task CORE-591. Splitting them is only
// safe if the parameter vocabulary is fixed FIRST — otherwise each surface
// invents its own spelling of `section` and the dashboard aggregates garbage.
// So every event below takes a typed argument object rather than a loose
// `Record<string, unknown>`: a wrong section name is a compile error, not a
// silently-empty dashboard row. The admin dashboard groups by exactly these
// property names (`AnalyticsQueryService.GetHomeDashboardAsync`), and the BE
// mirror of the names lives in `Agape.Shared/Models/AnalyticsEventNames.cs`.
//
// ── Privacy (§9, last line) ─────────────────────────────────────────────────
// «Не отправлять персональные тексты поиска и чувствительные данные без
// отдельной политики аналитики.» Enforced twice on purpose: `trackSearch-
// Submitted` has no parameter that could carry the query (only its length), and
// `scrubSensitive` strips free-text keys from every payload as a backstop, so a
// later careless `{ ...props, query }` at a call site cannot leak the text.

import { analytics } from './analytics';

/**
 * Canonical event names. Values are the exact strings persisted to
 * `AnalyticsEvents.EventName` — dashboards key off them, so they are stable.
 *
 * All eleven names come verbatim from §9. `actionResult` is the one addition:
 * §9 asks for «home_live_join_clicked, home_event_rsvp_clicked + результат»,
 * and a click and its outcome cannot share one row without losing whichever
 * happens second (a guest who never resolves the soft-auth would otherwise be
 * indistinguishable from a successful RSVP). One extra name covers both
 * actions instead of two.
 */
export const HomeEvents = {
  viewed: 'home_viewed',
  sectionViewed: 'home_section_viewed',
  cardClicked: 'home_card_clicked',
  ctaClicked: 'home_cta_clicked',
  liveJoinClicked: 'home_live_join_clicked',
  eventRsvpClicked: 'home_event_rsvp_clicked',
  actionResult: 'home_action_result',
  softAuthOpened: 'soft_auth_opened',
  softAuthActionSelected: 'soft_auth_action_selected',
  searchOpened: 'home_search_opened',
  searchSubmitted: 'home_search_submitted',
  dataLoadFailed: 'home_data_load_failed',
} as const;

export type HomeEventName = (typeof HomeEvents)[keyof typeof HomeEvents];

/**
 * The blocks of the new home, one per §6.1–6.12 plus the closing CTA. This is
 * the only accepted spelling of `section` — the dashboard's per-section rows
 * are built from this list, so a section missing here shows up as an
 * «(unknown)» row rather than being silently dropped.
 */
export const HOME_SECTIONS = [
  'header',            // 6.1  Шапка
  'hero',              // 6.2  Hero
  'app_download',      // CORE-1548 «Agapella всегда рядом» — блок скачивания приложения
  'how_it_works',      // 6.3  «Как работает наша платформа?»
  'live_now',          // 6.4  «Сейчас в эфире»
  'upcoming_events',   // 6.5  «Ожидаемые события»
  'featured_masters',  // 6.6  «Популярные мастера»
  'new_masters',       // 6.7  «Новые мастера»
  'communities',       // 6.8  «Сообщества» (за feature flag в Release 1)
  'news',              // 6.9  «Новости Agapella»
  'environment',       // 6.10 «Найди своё окружение»
  'capabilities',      // 6.11 «Возможности Agapella»
  'light_path',        // 6.12 «Иди по пути света вместе с Agapella»
  'final_cta',         // финальный CTA
] as const;

export type HomeSection = (typeof HOME_SECTIONS)[number];

/** What a clicked card points at. Drives `entityType` on `home_card_clicked`. */
export type HomeEntityType =
  | 'live'
  | 'event'
  | 'master'
  | 'news'
  | 'community'
  | 'capability';

/**
 * Whether the actor was signed in at the moment of the action. The whole point
 * of the dashboard is the guest→auth conversion, so this is required on every
 * event a guest can trigger.
 */
export type AuthState = 'guest' | 'authenticated';

/** Which personal action opened soft-auth (§2.1: Live, RSVP, AI-чат, Путь света). */
export type SoftAuthSource =
  | 'live_join'
  | 'event_rsvp'
  | 'ai_chat'
  | 'light_path'
  | 'search'
  | 'profile';

/** What the user picked inside the soft-auth sheet. */
export type SoftAuthAction = 'login' | 'register' | 'social' | 'dismissed';

/** The two personal actions §9 wants an outcome for. */
export type HomeAction = 'live_join' | 'event_rsvp';

/** Outcome of a personal action. `soft_auth` = a guest was routed to sign-in. */
export type HomeActionResult = 'success' | 'soft_auth' | 'error';

// ── Privacy backstop ────────────────────────────────────────────────────────

/**
 * Property keys that may hold user-typed text. §9 forbids sending them, and no
 * helper below accepts one — this only guards against a future call site that
 * spreads extra props in.
 */
const FREE_TEXT_KEYS = ['query', 'q', 'text', 'searchText', 'term', 'keyword', 'email', 'phone'];

/** Drop free-text keys from a payload. Exported for the delivery test. */
export function scrubSensitive<T extends Record<string, unknown>>(props: T): T {
  const out = { ...props };
  for (const key of Object.keys(out)) {
    if (FREE_TEXT_KEYS.includes(key)) delete out[key];
  }
  return out;
}

function emit(event: HomeEventName, props?: Record<string, unknown>): void {
  analytics.track(event, props ? (scrubSensitive(props) as Record<string, any>) : undefined);
}

// ── Emit helpers ────────────────────────────────────────────────────────────

/** The home was opened. `authState` splits the guest funnel from the signed-in one. */
export function trackHomeViewed(authState: AuthState): void {
  emit(HomeEvents.viewed, { authState });
}

/**
 * A section scrolled into view. Emit ONCE per section per page view — the
 * dashboard reads section impressions as the denominator of card CTR, so a
 * re-fire on every scroll tick would deflate every rate on the board.
 */
export function trackSectionViewed(section: HomeSection): void {
  emit(HomeEvents.sectionViewed, { section });
}

/** A card inside a section was clicked. `position` is 0-based within the section. */
export function trackCardClicked(args: {
  section: HomeSection;
  entityType: HomeEntityType;
  entityId: string;
  position: number;
}): void {
  emit(HomeEvents.cardClicked, args);
}

/** A call-to-action button was clicked. `cta` is a stable slug, e.g. 'hero_primary'. */
export function trackCtaClicked(args: {
  cta: string;
  authState: AuthState;
  section?: HomeSection;
}): void {
  emit(HomeEvents.ctaClicked, args);
}

/** «Присоединиться» on a live card. Pair with {@link trackActionResult}. */
export function trackLiveJoinClicked(args: {
  entityId: string;
  authState: AuthState;
  position?: number;
}): void {
  emit(HomeEvents.liveJoinClicked, args);
}

/** RSVP on an upcoming event. Pair with {@link trackActionResult}. */
export function trackEventRsvpClicked(args: {
  entityId: string;
  authState: AuthState;
  position?: number;
}): void {
  emit(HomeEvents.eventRsvpClicked, args);
}

/** Outcome of a live-join or RSVP. `errorCode` only on `result: 'error'`. */
export function trackActionResult(args: {
  action: HomeAction;
  result: HomeActionResult;
  entityId: string;
  errorCode?: string;
}): void {
  emit(HomeEvents.actionResult, args);
}

/** Soft-auth was shown because a guest attempted a personal action. */
export function trackSoftAuthOpened(source: SoftAuthSource): void {
  emit(HomeEvents.softAuthOpened, { source });
}

/** What the guest chose in the soft-auth sheet. */
export function trackSoftAuthActionSelected(args: {
  source: SoftAuthSource;
  action: SoftAuthAction;
}): void {
  emit(HomeEvents.softAuthActionSelected, args);
}

/** The search field was focused/opened. */
export function trackSearchOpened(authState: AuthState): void {
  emit(HomeEvents.searchOpened, { authState });
}

/**
 * A search was submitted. Takes the query's LENGTH, never the query — §9
 * forbids sending search text, and there is deliberately no parameter that
 * could carry it. `resultCount` is a count, not content.
 */
export function trackSearchSubmitted(args: {
  queryLength: number;
  authState: AuthState;
  resultCount?: number;
}): void {
  emit(HomeEvents.searchSubmitted, args);
}

/**
 * A section failed to load. `errorCode` is the machine-readable code from the
 * aggregator's `sectionErrors` (§7), not a message — messages are unbounded
 * text and would make the error breakdown unaggregatable.
 */
export function trackDataLoadFailed(args: {
  section: HomeSection;
  errorCode: string;
}): void {
  emit(HomeEvents.dataLoadFailed, args);
}
